import { ARTIST_PORTAL_ROUTE } from 'config'

const STORAGE_KEY_PREFIX = 'artistProfileCompletion:'

export const SECTION_KEYS = {
  ACCOUNT: 'account_details',
  EXPERIENCE_SKILLS: 'experience_skills',
  PROGRAM_DELIVERY: 'program_delivery',
}

export const SECTION_META = {
  [SECTION_KEYS.ACCOUNT]: {
    label: 'Account Details',
    tooltip:
      'All fields with an asterisk are required to complete this section.',
    route: ARTIST_PORTAL_ROUTE + '/profile/account',
  },
  [SECTION_KEYS.EXPERIENCE_SKILLS]: {
    label: 'Experience & Skills',
    tooltip:
      'Education, Experience, and Skills & Offerings are required to complete this section.',
    route: ARTIST_PORTAL_ROUTE + '/profile/experience-skills',
  },
  [SECTION_KEYS.PROGRAM_DELIVERY]: {
    label: 'Program Delivery',
    tooltip: 'Add at least one program to complete this section.',
    route: ARTIST_PORTAL_ROUTE + '/profile/program-delivery',
  },
}

export function getSections() {
  return [
    SECTION_KEYS.ACCOUNT,
    SECTION_KEYS.EXPERIENCE_SKILLS,
    SECTION_KEYS.PROGRAM_DELIVERY,
  ]
}

function hasEducationComplete(credentials) {
  if (!Array.isArray(credentials)) return false
  return credentials.some(
    (credential) =>
      credential.credentialType === 'degree' &&
      credential.institutionName &&
      credential.credentialName
  )
}

function hasExperienceComplete(experiences) {
  if (!Array.isArray(experiences)) return false
  return experiences.some(
    (experience) =>
      experience.title &&
      experience.ftPtCo &&
      experience.organizationName &&
      experience.fullLocation &&
      experience.startDate &&
      experience.endDate
  )
}

function hasSkillsAndOfferingsComplete(skills) {
  const skillGroups = skills || {}
  return [
    skillGroups.approaches,
    skillGroups.outcomes,
    skillGroups.languages,
    skillGroups.subDisciplines,
    skillGroups.programTypes,
  ].some((arr) => Array.isArray(arr) && arr.length > 0)
}

export function computePercent(completedCount, totalCount) {
  if (!totalCount) return 0
  if (completedCount <= 0) return 0
  return Math.round((completedCount / totalCount) * 100)
}

export function isSectionComplete(sectionKey, artist) {
  if (!artist) return false

  switch (sectionKey) {
    case SECTION_KEYS.ACCOUNT: {
      const profile = artist.profile || {}
      const detail = artist.detail || {}
      const address = artist.address || {}
      const hasYearsOfExperience =
        profile.yearsOfExperience !== null &&
        profile.yearsOfExperience !== undefined &&
        profile.yearsOfExperience !== ''

      return Boolean(
        profile.firstName &&
          profile.lastName &&
          detail.phone &&
          artist.profilePic &&
          address.street &&
          address.city &&
          address.state &&
          address.zip &&
          hasYearsOfExperience &&
          artist.description
      )
    }
    case SECTION_KEYS.EXPERIENCE_SKILLS:
      return (
        hasEducationComplete(artist.credentials) &&
        hasExperienceComplete(artist.experiences) &&
        hasSkillsAndOfferingsComplete(artist.skills)
      )
    case SECTION_KEYS.PROGRAM_DELIVERY:
      return (
        (Array.isArray(artist.partnerships) &&
          artist.partnerships.length > 0) ||
        (Array.isArray(artist.taPrograms) && artist.taPrograms.length > 0)
      )
    default:
      return false
  }
}

export function deriveCompletedSections(artist) {
  return getSections().filter((sectionKey) =>
    isSectionComplete(sectionKey, artist)
  )
}

function storageKey(artistId) {
  return `${STORAGE_KEY_PREFIX}${artistId}`
}

export function setStoredProfileCompletion(artistId, status) {
  if (!artistId || typeof window === 'undefined') return
  window.localStorage.setItem(storageKey(artistId), JSON.stringify(status))
}

export function buildBarModel(artist) {
  const sections = getSections()
  const completedSections = deriveCompletedSections(artist)
  const completedSet = new Set(completedSections)
  const orderedCompletedSections = sections.filter((sectionKey) =>
    completedSet.has(sectionKey)
  )
  const orderedIncompleteSections = sections.filter(
    (sectionKey) => !completedSet.has(sectionKey)
  )
  const orderedSections = [
    ...orderedCompletedSections,
    ...orderedIncompleteSections,
  ]

  return {
    sections: orderedSections.map((key) => ({ key, ...SECTION_META[key] })),
    completedSections: orderedCompletedSections,
    percent: computePercent(completedSections.length, sections.length),
    totalCount: sections.length,
  }
}
